/**
 * Frosted-glass parity — semi-transparent themed layers (no native backdrop blur).
 * Same visual language on iOS and Android; replaces `expo-blur`.
 */
import { Platform, StyleSheet, type StyleProp, type ViewProps, type ViewStyle } from 'react-native';
import { appTheme } from './appTheme';

/** Tint presets used across headers, glass CTAs, and bottom chrome. */
export type BlurTint =
  | 'light'
  | 'dark'
  | 'default'
  | 'extraLight'
  | 'systemChromeMaterial'
  | 'systemChromeMaterialLight'
  | 'systemChromeMaterialDark'
  | 'systemMaterial'
  | 'systemMaterialLight'
  | 'systemMaterialDark'
  | 'systemUltraThinMaterial'
  | 'systemUltraThinMaterialLight'
  | 'systemUltraThinMaterialDark'
  | 'systemThinMaterial'
  | 'systemThinMaterialLight'
  | 'systemThinMaterialDark'
  | 'systemThickMaterial'
  | 'systemThickMaterialLight'
  | 'systemThickMaterialDark';

export type AndroidFrostedSurfaceRole = 'chrome' | 'glass-filled' | 'glass-outline';
export type AndroidFrostedWeight = 'default' | 'heavy';

export type ParityBlurViewProps = {
  style?: StyleProp<ViewStyle>;
  tint?: BlurTint;
  /** 0–100 frosted strength (maps to layer opacity). */
  intensity?: number;
  pointerEvents?: ViewProps['pointerEvents'];
  /** Android frosted layer recipe — glass CTAs should use `glass-outline`. */
  surfaceRole?: AndroidFrostedSurfaceRole;
};

/** Parity blur (iOS `BlurView` + Android frosted layers) is always available. */
export function shouldRenderNativeFrostedBlur(): boolean {
  return true;
}

/** Wrapper props that reduce Android clip/compositing seams around frosted layers. */
export function blurViewWrapperProps(): ViewStyle & { collapsable?: boolean } {
  if (Platform.OS === 'android') {
    return { collapsable: false, overflow: 'hidden' };
  }
  return { overflow: 'hidden' };
}

/**
 * Convert app blur amount (e.g. AppHeader DEFAULT = 5) to frosted intensity 1–100.
 * Android uses stronger intensity to match iOS frosted chrome readability.
 */
export function blurIntensityForPlatform(blur_amount: number): number {
  const base = Math.max(1, Math.round(blur_amount * 10));
  if (Platform.OS === 'android') {
    // Previous 2.5× multiplier made frosted CTAs look like solid grey panels.
    return Math.min(100, Math.max(4, Math.round(blur_amount * 10 * 1.1)));
  }
  return Math.min(100, base);
}

export function frostOpacityFromIntensity(intensity?: number): number {
  const value = intensity ?? 50;
  // Low blur amounts (glass CTAs ~3) must stay translucent — not 32%+ opaque washes.
  return Math.min(0.72, Math.max(0.1, value / 155));
}

export function isDarkBlurTint(tint: BlurTint): boolean {
  if (tint === 'dark') {
    return true;
  }
  return /dark/i.test(tint);
}

/** Semi-opaque fallback when native blur is unavailable. */
export function androidFrostedFallbackBackground(is_dark: boolean): string {
  return is_dark ? 'rgba(18, 18, 22, 0.78)' : 'rgba(255, 255, 255, 0.72)';
}

function with_rgba_alpha(color: string, alpha: number): string {
  const match = color.match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i);
  if (!match) {
    return color;
  }
  const clamped = Math.min(1, Math.max(0, alpha));
  return `rgba(${match[1]}, ${match[2]}, ${match[3]}, ${clamped})`;
}

function parse_rgba_alpha(color: string): number | null {
  const match = color.match(/rgba?\(\s*[\d.]+\s*,\s*[\d.]+\s*,\s*[\d.]+\s*,\s*([\d.]+)\s*\)/i);
  if (!match) {
    return null;
  }
  return parseFloat(match[1]);
}

/** Heavier glass fill on Android — keeps light/dark label text readable over busy hero photos. */
function boost_android_glass_tint(tint: string, is_dark: boolean): string {
  if (!tint || tint === 'transparent') {
    return is_dark ? 'rgba(255, 255, 255, 0.24)' : 'rgba(255, 255, 255, 0.36)';
  }
  const alpha = parse_rgba_alpha(tint);
  if (alpha === null) {
    return tint;
  }
  const boosted = is_dark ? Math.min(0.42, alpha + 0.2) : Math.min(0.44, alpha + 0.22);
  return with_rgba_alpha(tint, boosted);
}

function android_heavy_chrome_fill(is_dark: boolean): string {
  return is_dark ? 'rgba(12, 12, 16, 0.97)' : 'rgba(255, 255, 255, 0.95)';
}

/**
 * Layered frosted simulation (iOS + Android). Premium readable glass without native blur.
 */
export function resolveAndroidFrostedSurfaceLayers(
  is_dark: boolean,
  role: AndroidFrostedSurfaceRole = 'chrome',
  caller_tint?: string | null,
  weight: AndroidFrostedWeight = 'default',
): { depth_wash: string | null; fill: string } {
  const heavy = weight === 'heavy';

  switch (role) {
    case 'glass-outline': {
      const base_fill =
        caller_tint && caller_tint !== 'transparent'
          ? caller_tint
          : is_dark
            ? 'rgba(255, 255, 255, 0.1)'
            : 'rgba(255, 255, 255, 0.14)';
      return {
        depth_wash: heavy
          ? is_dark
            ? 'rgba(0, 0, 0, 0.22)'
            : 'rgba(0, 0, 0, 0.14)'
          : is_dark
            ? 'rgba(0, 0, 0, 0.12)'
            : 'rgba(0, 0, 0, 0.05)',
        fill: heavy ? boost_android_glass_tint(base_fill, is_dark) : base_fill,
      };
    }
    case 'glass-filled': {
      const base_fill =
        caller_tint && caller_tint !== 'transparent'
          ? caller_tint
          : is_dark
            ? 'rgba(28, 28, 32, 0.88)'
            : 'rgba(255, 255, 255, 0.82)';
      return {
        depth_wash: heavy ? (is_dark ? 'rgba(0, 0, 0, 0.24)' : 'rgba(0, 0, 0, 0.14)') : null,
        fill: heavy ? boost_android_glass_tint(base_fill, is_dark) : base_fill,
      };
    }
    case 'chrome':
    default:
      return {
        depth_wash: heavy ? (is_dark ? 'rgba(0, 0, 0, 0.18)' : 'rgba(0, 0, 0, 0.08)') : null,
        fill: heavy ? android_heavy_chrome_fill(is_dark) : androidFrostedFallbackBackground(is_dark),
      };
  }
}

export function resolveFrostedLayersForTint(
  tint: BlurTint,
  role: AndroidFrostedSurfaceRole = 'chrome',
  weight: AndroidFrostedWeight = 'default',
): { depth_wash: string | null; fill: string } {
  const is_dark = isDarkBlurTint(tint);
  const use_heavy =
    weight === 'heavy' ||
    tint.includes('Thick') ||
    tint.includes('ChromeMaterial') ||
    tint === 'systemMaterial';
  return resolveAndroidFrostedSurfaceLayers(is_dark, role, null, use_heavy ? 'heavy' : 'default');
}

/** Menu / daily-menu header shell at scroll rest. */
export function androidFrostedHeaderShellRest(is_dark: boolean, weight: AndroidFrostedWeight = 'default'): string {
  if (weight === 'heavy') {
    return is_dark ? 'rgba(12, 12, 16, 0.90)' : 'rgba(255, 255, 255, 0.94)';
  }
  return is_dark ? 'rgba(18, 18, 22, 0.72)' : 'rgba(255, 255, 255, 0.82)';
}

export function resolveBlurTint(is_dark: boolean): BlurTint {
  return is_dark ? 'dark' : 'light';
}

type GlassBorderRadii = Pick<
  ViewStyle,
  'borderRadius' | 'borderTopLeftRadius' | 'borderTopRightRadius' | 'borderBottomLeftRadius' | 'borderBottomRightRadius'
>;

/** Accent hairline on frosted shells. */
export function glassAccentBorderStyle(border_color: string, radii: GlassBorderRadii = {}): ViewStyle {
  return {
    borderWidth: Platform.OS === 'android' ? StyleSheet.hairlineWidth : 1,
    borderColor: border_color,
    ...radii,
  };
}

/** Android elevation + shadow often render as harsh horizontal seams on rounded blocks. */
export function androidStripElevationSeams(): ViewStyle {
  if (Platform.OS !== 'android') {
    return {};
  }
  return {
    elevation: 0,
    shadowColor: 'transparent',
    shadowOpacity: 0,
    shadowRadius: 0,
    shadowOffset: { width: 0, height: 0 },
  };
}

/** Frosted header bottom rule — hairline reads as a bright stroke over blur on Android. */
export function androidHideFrostedChromeHairline(): ViewStyle {
  if (Platform.OS !== 'android') {
    return {};
  }
  return { borderBottomWidth: 0 };
}

/** Menu date slider / hero top inset — full safe-area padding is excessive on Android status bars. */
export function androidReducedSafeAreaTopInset(full_inset: number, max_inset = 8): number {
  if (Platform.OS !== 'android') {
    return full_inset;
  }
  return Math.min(full_inset, max_inset);
}

/**
 * Menu / daily-menu date band height (content + status-bar clearance via flex-end).
 * Safe area is owned here / by set2 header wrap — DateSlider must not also pad `insets.top`.
 */
export const MENU_FLOW_HEADER_DATE_BLOCK_HEIGHT = Platform.OS === 'android' ? 125 : 160;

/** iOS-style soft shadow on frosted chrome; flat on Android (elevation seams). */
export function frostedChromeShadow(shadow: ViewStyle): ViewStyle {
  if (Platform.OS === 'android') {
    return androidStripElevationSeams();
  }
  return shadow;
}

/** Shared corner radii for frosted bottom chrome (menu/order cart bars). */
export const FROSTED_BOTTOM_CHROME_RADIUS: Pick<ViewStyle, 'borderTopLeftRadius' | 'borderTopRightRadius'> = {
  borderTopLeftRadius: 24,
  borderTopRightRadius: 24,
};
